import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import Alert from '../../../components/common/Alert';
import TableSite from '../../../components/common/Table';
import SimpleTabs from '../../../components/common/Tabs';
import PremioAPI from '../premio-api';
import '../../../assets/styles/content/convocatoria-postulantes.scss';
import LinkComponent from '../../../components/common/Link';
import { Modal, Snackbar, IconButton } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Button from '../../../components/common/Button';
import ModalContent from '../../../components/common/Modal/ModalContent';
import Menu, { MenuContainer } from '../../../components/common/Menu';
import Spinner from '../../../components/common/Spinner';
import FinalTab from '../FinalTab';
import FeedbackImport from './FeedbackImport';

const STATUS_METADATA = {
  DRAFT: { label: 'Borrador', variant: 'draft' },
  SUBMITTED: { label: 'Enviada', variant: 'submitted' },
  UNDER_REVIEW: { label: 'En revisión', variant: 'review' },
  FINALIST: { label: 'Finalista', variant: 'finalist' },
  AWARDED: { label: 'Premiada', variant: 'awarded' },
  REJECTED: { label: 'Rechazada', variant: 'rejected' },
};

const STEP_LABELS = {
  0: 'Formulario',
  1: 'Chat',
  2: 'Pitch',
};

const STEP_EDIT_DISABLED_STATUSES = new Set(['SUBMITTED', 'ENVIADA']);

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return moment(dateString).format('DD-MM-YYYY, h:mm a');
};

const mapApplication = (application) => {
  if (!application) return null;
  const meta =
    STATUS_METADATA[application.status] || {
      label: application.status,
      variant: 'default',
    };
  const parsedStep = Number(application.step_actual);
  const normalizedStep = Number.isFinite(parsedStep) ? parsedStep : null;

  return {
    application_id: application.application_id,
    convocatoria_id: application.convocatoria_id,
    applicant_name: application.applicant_name,
    applicant_email: application.applicant_email,
    status: application.status,
    step_actual: normalizedStep,
    pitch_score:
      typeof application.pitch_score === 'number' && Number.isFinite(application.pitch_score)
        ? application.pitch_score
        : null,
    status_label: meta.label,
    status_variant: meta.variant,
    submitted_at: formatDate(application.submitted_at),
    created_at: formatDate(application.created_at),
    updated_at: formatDate(application.updated_at),
  };
};

const ConvocatoriaPostulantesList = () => {
  const { convocatoriaId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [convocatoria, setConvocatoria] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingStepId, setUpdatingStepId] = useState(null);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [isStatusesDownloading, setIsStatusesDownloading] = useState(false);
  const [isStepUpdatingOverlay, setIsStepUpdatingOverlay] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState({ open: false, id: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openStepDropdownId, setOpenStepDropdownId] = useState(null);
  const [pendingStepUpdate, setPendingStepUpdate] = useState({ application: null, step: null });
  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const closeMenu = useCallback(() => {
    setIsMenuOpen({ open: false, id: '' });
  }, []);


  const renderMenuLoadingLabel = useCallback(
    (label) => (
      <span className="convocatoria-postulantes__menu-loading">
        <Spinner
          size={16}
          style={{
            display: 'inline-flex',
            width: 'auto',
            height: 'auto',
            marginRight: 8,
            justifyContent: 'flex-start',
          }}
        />
        <span>{label}</span>
      </span>
    ),
    []
  );

  const getAvailableStepOptions = useCallback((currentStep) => {
    if (typeof currentStep !== 'number') return [];
    return Object.keys(STEP_LABELS)
      .map((value) => Number(value))
      .filter((stepValue) => stepValue !== currentStep);
  }, []);

  const handleToggleStepDropdown = useCallback(
    (application) => {
      const options = getAvailableStepOptions(application.step_actual);
      if (!options.length) return;
      setOpenStepDropdownId((prev) =>
        prev === application.application_id ? null : application.application_id
      );
    },
    [getAvailableStepOptions]
  );

  const closeStepDropdown = useCallback(() => {
    setOpenStepDropdownId(null);
  }, []);

  const fetchApplications = useCallback(
    async (withLoader = true) => {
      try {
        if (withLoader) {
          setIsLoading(true);
        }
        const response = await PremioAPI.fetchConvocatoriaApplications(convocatoriaId);
        const list = (response.data || []).map(mapApplication).filter(Boolean);
        setApplications(list);
        setError('');
      } catch (fetchError) {
        const detail =
          fetchError?.data?.detail ||
          fetchError?.data?.message ||
          fetchError?.statusText ||
          'No fue posible obtener las postulaciones.';
        setError(detail);
      } finally {
        if (withLoader) {
          setIsLoading(false);
        }
      }
    },
    [convocatoriaId]
  );

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('.convocatoria-postulantes__step-trigger')) {
        closeStepDropdown();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [closeStepDropdown]);

  const handleStepChange = useCallback(
    async (application, newStepValue, { showOverlay = false } = {}) => {
      const numericStep = Number(newStepValue);
      if (!Number.isFinite(numericStep)) return;
      try {
        setUpdatingStepId(application.application_id);
        if (showOverlay) {
          setIsStepUpdatingOverlay(true);
        }
        const response = await PremioAPI.updateApplicationStep(application.application_id, {
          targetStep: numericStep,
        });
        const apiApplication = mapApplication(response?.data || response);
        const updatedApplication = {
          ...application,
          ...(apiApplication || {}),
          step_actual: numericStep,
        };
        setApplications((prev) =>
          prev.map((item) =>
            item.application_id === updatedApplication.application_id ? updatedApplication : item
          )
        );
        await fetchApplications(false);
        showSnackbar('El paso de la postulación se actualizó correctamente.', 'success');
      } catch (updateErr) {
        const detail =
          updateErr?.data?.detail ||
          updateErr?.data?.message ||
          updateErr?.statusText ||
          'No fue posible actualizar el paso de la postulación.';
        showSnackbar(detail, 'error');
      } finally {
        setUpdatingStepId(null);
        setIsStepUpdatingOverlay(false);
        closeStepDropdown();
      }
    },
    [closeStepDropdown, fetchApplications, showSnackbar]
  );

  const handleRequestStepChange = useCallback(
    (application, step) => {
      closeStepDropdown();
      const numericStep = Number(step);
      if (!Number.isFinite(numericStep)) return;
      setPendingStepUpdate({ application: mapApplication(application) || application, step: numericStep });
      setIsStepModalOpen(true);
    },
    [closeStepDropdown]
  );

  const handleCloseStepModal = useCallback(() => {
    setIsStepModalOpen(false);
    setPendingStepUpdate({ application: null, step: null });
  }, []);

  const handleConfirmStepChange = useCallback(async () => {
    if (!pendingStepUpdate.application) return;
    await handleStepChange(pendingStepUpdate.application, pendingStepUpdate.step, {
      showOverlay: true,
    });
    handleCloseStepModal();
  }, [handleCloseStepModal, handleStepChange, pendingStepUpdate]);

  const toggleMenu = useCallback((applicationId) => {
    setIsMenuOpen((prev) => ({
      open: prev.id === applicationId ? !prev.open : true,
      id: applicationId,
    }));
  }, []);

  const handleOpenDeleteModal = useCallback(
    (application) => {
      setApplicationToDelete(application);
      setIsDeleteModalOpen(true);
      closeMenu();
    },
    [closeMenu]
  );

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setApplicationToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!applicationToDelete) return;
    const { application_id } = applicationToDelete;

    try {
      setDeletingId(application_id);
      await PremioAPI.deleteApplication(application_id);
      setApplications((prev) =>
        prev.filter((application) => application.application_id !== application_id)
      );
      handleCloseDeleteModal();
      showSnackbar('La postulación se eliminó correctamente.', 'success');
    } catch (deleteErr) {
      const detail =
        deleteErr?.data?.detail ||
        deleteErr?.data?.message ||
        deleteErr?.statusText ||
        'No pudimos eliminar la postulación, inténtalo más tarde.';
      showSnackbar(detail, 'error');
    } finally {
      setDeletingId(null);
    }
  }, [applicationToDelete, handleCloseDeleteModal, showSnackbar]);

  const convocatoriaTitle = convocatoria?.title || location.state?.title || convocatoriaId;
  const isEmerge = convocatoria?.id === convocatoriaId && convocatoria?.slug === 'emerge';
  const isCategoryVoting = convocatoria?.id === convocatoriaId && ['emerge', 'emerge-demo'].includes(convocatoria?.slug);

  useEffect(() => {
    let active = true;
    setConvocatoria(null);
    PremioAPI.fetchConvocatoria(convocatoriaId)
      .then((response) => { if (active) setConvocatoria(response.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [convocatoriaId]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleDownload = useCallback(async (application) => {
    const { application_id, applicant_name } = application;
    try {
      setDownloadingId(application_id);
      const response = await PremioAPI.downloadApplicationExport(application_id);
      const responseData = response?.data instanceof Blob ? response.data : response;
      const blob = responseData instanceof Blob ? responseData : new Blob([responseData]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const slug = (applicant_name || application_id)
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '') || application_id;
      link.href = url;
      link.setAttribute('download', `postulacion-${slug}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Descarga de la postulación bajada con exíto.', 'success');
    } catch (downloadErr) {
      const detail =
        downloadErr?.data?.detail ||
        downloadErr?.data?.message ||
        downloadErr?.statusText ||
        'No fue posible descargar la postulación.';
      showSnackbar(detail, 'error');
    } finally {
      setDownloadingId(null);
    }
  }, [showSnackbar]);

  const columns = useMemo(
    () => [
      {
        Header: 'Nombre del postulante',
        accessor: 'applicant_name',
      },
      {
        Header: 'Email',
        accessor: 'applicant_email',
      },
      {
        Header: 'Iniciado',
        accessor: 'created_at',
      },
      {
        Header: 'Actualizado',
        accessor: 'updated_at',
      },
      {
        Header: 'Finalizado',
        accessor: 'submitted_at',
      },
      {
        Header: 'Estado',
        accessor: 'status_label',
        Cell: ({ row }) => (
          <span
            className={`convocatoria-postulantes__status convocatoria-postulantes__status--${row.original.status_variant}`}
          >
            {isEmerge && row.original.status === 'REJECTED' ? 'No seleccionada' : row.original.status_label}
          </span>
        ),
      },
      {
        Header: 'Paso',
        accessor: 'step_actual',
        Cell: ({ row }) => {
          const { step_actual } = row.original;
          const applicationId = row.original.application_id;
          const normalizedStatus = typeof row.original.status === 'string'
            ? row.original.status.toUpperCase()
            : '';
          const isStepEditAllowed = !STEP_EDIT_DISABLED_STATUSES.has(normalizedStatus);
          const stepLabel =
            typeof step_actual === 'number' ? STEP_LABELS?.[step_actual] || '-' : '-';
          const availableOptions = getAvailableStepOptions(step_actual);
          const isUpdatingStep = updatingStepId === applicationId;
          const isDropdownOpen = openStepDropdownId === applicationId;

          return (
            <div className="convocatoria-postulantes__step-cell">
              <span>{stepLabel}</span>
              {isStepEditAllowed &&
                (isUpdatingStep ? (
                  <Spinner
                    size={16}
                    style={{
                      display: 'inline-flex',
                      width: 'auto',
                      height: 'auto',
                    }}
                  />
                ) : (
                  <div className="convocatoria-postulantes__step-trigger">
                    <IconButton
                      size="small"
                      className="convocatoria-postulantes__step-edit"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleStepDropdown(row.original);
                      }}
                      disabled={!availableOptions.length}
                    >
                      <span className="material-icons">edit</span>
                    </IconButton>
                    {isDropdownOpen && availableOptions.length > 0 && (
                      <div className="convocatoria-postulantes__step-dropdown">
                        {availableOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRequestStepChange(row.original, option);
                            }}
                          >
                            {STEP_LABELS[option]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          );
        },
      },
      {
        Header: 'Puntaje pitch',
        accessor: 'pitch_score',
        Cell: ({ row }) =>
          row.original.pitch_score != null ? (
            <span className="convocatoria-postulantes__score">
              {row.original.pitch_score.toFixed(2)} / 10
            </span>
          ) : (
            <span className="convocatoria-postulantes__score convocatoria-postulantes__score--empty">
              -
            </span>
          ),
      },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row }) => {
          const applicationId = row.original.application_id;
          const isCurrentMenuOpen = isMenuOpen.id === applicationId && isMenuOpen.open;
          const isDownloadingCurrent = downloadingId === applicationId;
          const isDeletingCurrent = deletingId === applicationId;

          return (
            <MenuContainer
              onClick={() => toggleMenu(applicationId)}
              isActive={isCurrentMenuOpen}
            >
              {isCurrentMenuOpen && (
                <Menu
                  open
                  items={[
                    {
                      text: isDownloadingCurrent
                        ? renderMenuLoadingLabel('Descargando...')
                        : 'Descargar postulación',
                      status: isDownloadingCurrent ? 'disabled' : '',
                      action: () => {
                        if (isDownloadingCurrent) return;
                        handleDownload(row.original);
                        closeMenu();
                      },
                    },
                    {
                      text: isDeletingCurrent
                        ? renderMenuLoadingLabel('Eliminando...')
                        : 'Eliminar postulación',
                      status: isDeletingCurrent ? 'disabled' : 'warning',
                      action: () => {
                        if (isDeletingCurrent) return;
                        handleOpenDeleteModal(row.original);
                      },
                    },
                  ]}
                />
              )}
            </MenuContainer>
          );
        },
      },
    ],
    [
      downloadingId,
      deletingId,
      handleDownload,
      handleOpenDeleteModal,
      isMenuOpen,
      toggleMenu,
      closeMenu,
      renderMenuLoadingLabel,
      handleStepChange,
      getAvailableStepOptions,
      updatingStepId,
      handleToggleStepDropdown,
      handleRequestStepChange,
      openStepDropdownId,
      isEmerge,
    ]
  );

  const handleBulkDownload = useCallback(async () => {
    try {
      setIsBulkDownloading(true);
      const response = await PremioAPI.downloadConvocatoriaExport(convocatoriaId);
      const responseData = response?.data instanceof Blob ? response.data : response;
      const blob = responseData instanceof Blob ? responseData : new Blob([responseData]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const slug = (convocatoriaTitle || convocatoriaId)
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '') || convocatoriaId;
      link.href = url;
      link.setAttribute('download', `convocatoria-${slug}-postulaciones.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Descarga masiva bajada con éxito.', 'success');
    } catch (downloadErr) {
      const detail =
        downloadErr?.data?.detail ||
        downloadErr?.data?.message ||
        downloadErr?.statusText ||
        'No fue posible descargar las postulaciones.';
      showSnackbar(detail, 'error');
    } finally {
      setIsBulkDownloading(false);
    }
  }, [convocatoriaId, convocatoriaTitle, showSnackbar]);

  const handleStatusesDownload = useCallback(async () => {
    if (isStatusesDownloading) return;

    try {
      setIsStatusesDownloading(true);
      const response = await PremioAPI.downloadConvocatoriaStatuses(convocatoriaId);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const slug = (convocatoriaTitle || convocatoriaId)
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '') || convocatoriaId;

      try {
        link.href = url;
        link.download = `convocatoria-${slug}-estados.xlsx`;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      showSnackbar('La descarga de estados se inició correctamente.', 'success');
    } catch (downloadErr) {
      let errorData = downloadErr?.data;
      if (errorData instanceof Blob) {
        try {
          errorData = JSON.parse(await errorData.text());
        } catch (_) {
          errorData = null;
        }
      }
      const detail = errorData?.detail || errorData?.message;
      showSnackbar(
        typeof detail === 'string' ? detail : 'No fue posible descargar los estados.',
        'error'
      );
    } finally {
      setIsStatusesDownloading(false);
    }
  }, [convocatoriaId, convocatoriaTitle, isStatusesDownloading, showSnackbar]);

  const isActionLoading = Boolean(
    isBulkDownloading || isStatusesDownloading || downloadingId || deletingId || isStepUpdatingOverlay
  );

  const tabs = [{ title: 'Postulaciones' }, { title: 'Final' }];
  const contentTabs = [
    <div key="postulaciones" className="convocatoria-postulantes">
      {error && <Alert type="error">{error}</Alert>}
      <TableSite
        data={applications}
        columns={columns}
        isLoadingData={isLoading}
        tableName={`Postulaciones - ${convocatoriaTitle}`}
        headerActions={
          <>
            {isEmerge && (
              <FeedbackImport
                key={convocatoriaId}
                convocatoriaId={convocatoriaId}
                title={convocatoriaTitle}
                disabled={isLoading || isActionLoading}
                onImported={() => fetchApplications(false)}
              />
            )}
            <button
              type="button"
              className="button secondary convocatoria-postulantes__download-statuses"
              onClick={handleStatusesDownload}
              disabled={isLoading || isActionLoading}
              aria-busy={isStatusesDownloading}
            >
              {isStatusesDownloading ? 'Descargando Estados…' : 'Descargar Estados'}
            </button>
          </>
        }
        buttonLabel="Descargar Postulaciones"
        onClickButton={handleBulkDownload}
      />
    </div>,
    <FinalTab key="final" convocatoriaId={convocatoriaId} isCategoryVoting={isCategoryVoting} isDemo={convocatoria?.id === convocatoriaId && convocatoria?.slug === 'emerge-demo'} />,
  ];

  return (
    <div className="user-container">
      {isActionLoading && (
        <Spinner
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1400,
          }}
          size={56}
        />
      )}
      <div className="client__back-container">
        <LinkComponent title="Volver" url="/premio" icon="arrow_back" />
      </div>
      <SimpleTabs tabs={tabs} content={contentTabs} />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
      {isStepModalOpen && pendingStepUpdate.application && (
        <Modal open={isStepModalOpen} onClose={handleCloseStepModal}>
          <ModalContent title="Cambiar paso" onClick={handleCloseStepModal}>
            <p>
              ¿Deseas mover la postulación de{' '}
              <strong>{pendingStepUpdate.application.applicant_name || 'este postulante'}</strong> al
              paso <strong>{STEP_LABELS[pendingStepUpdate.step] || pendingStepUpdate.step}</strong>?
            </p>
            <div className="modal__action-container">
              <Button
                secondary
                onClick={handleCloseStepModal}
                style={{ minWidth: 100, padding: '6px 16px', fontSize: '13px' }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmStepChange}
                loading={updatingStepId === pendingStepUpdate.application.application_id}
                style={{ minWidth: 100, padding: '6px 16px', fontSize: '13px' }}
              >
                Confirmar
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
      {isDeleteModalOpen && applicationToDelete && (
        <Modal open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
          <ModalContent title="Eliminar postulación" onClick={handleCloseDeleteModal}>
            <p>
              ¿Estás seguro que deseas eliminar la postulación de{' '}
              <strong>{applicationToDelete.applicant_name || 'este postulante'}</strong>? Esta
              acción es irreversible.
            </p>
            <div className="modal__action-container">
              <Button
                secondary
                onClick={handleCloseDeleteModal}
                style={{ minWidth: 100, padding: '6px 16px', fontSize: '13px' }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDelete}
                loading={deletingId === applicationToDelete.application_id}
                style={{ minWidth: 100, padding: '6px 16px', fontSize: '13px' }}
              >
                Eliminar
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default ConvocatoriaPostulantesList;
